import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { FiPlus, FiEdit2, FiTrash2, FiImage, FiSave, FiX, FiList, FiGrid, FiActivity } from 'react-icons/fi';

const ServicesManager = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        categories: [],
        items: []
    });
    // Toggle state: true = Categorized (e.g. Med Derm), false = Simple List (e.g. Intimate Services)
    const [isCategorized, setIsCategorized] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);

    // Default data to seed if database is empty
    const DEFAULT_SERVICES = [
        {
            title: 'Intimate Services',
            items: [
                'Vaginal Rejuvination',
                'Post Delivery Pelvic Floor Rehabilitation',
                'Urinary Bladder Care',
                'Cervical Rejuvination',
                'Vulval Rejuvination'
            ]
        },
        {
            title: 'Laser & Advanced Procedures',
            items: [
                'CO2 Laser',
                'Nd:YAG Lazer',
                'Diode Lazer',
                'Lyper pigmentation treatment',
                'Diode laser',
                'Plasma Lazer'
            ]
        },
        {
            title: 'Advanced Skin & Aesthetics',
            items: [
                'Chemical Peels',
                'Botox & Injectables',
                'Fillers',
                'Exosomes',
                'PRP (Platelet Rich Plasma)',
                'GFC (Growth Factor Concentrate)',
                'Microneedling'
            ]
        },
        {
            title: 'Medical Dermatology',
            categories: [
                {
                    name: 'A. Skin Infection',
                    items: [
                        'Fungal Infection',
                        'Bacterial Infection',
                        'Viral',
                        'Parasite Infection',
                        'Bullous Disorder'
                    ]
                },
                {
                    name: 'B. Pigmentary Disorder',
                    items: [
                        '1. Hyper Pigmentation',
                        'Melasma',
                        'Freckles',
                        'Naevus',
                        'Acanthosis',
                        'Nigricans',
                        'Tanning',
                        '2. Hypo Pigmentation',
                        'Vitiligo',
                        'IGH',
                        'Mydriasis Alba'
                    ]
                },
                {
                    name: 'C. Acne & Acne Scar Management',
                    items: []
                }
            ]
        },
        {
            title: 'Hair & Scalp Care',
            items: [
                'Androgenic Alopecia',
                'Alopecia Areata',
                'Male Pattern Hair Loss',
                'Female Pattern Hair Loss'
            ]
        }
    ];

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            const servicesCol = collection(db, 'services');
            const snapshot = await getDocs(servicesCol);
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const sortedList = list.sort((a, b) => (a.order || 0) - (b.order || 0));
            setServices(sortedList);
        } catch (error) {
            console.error('Error fetching services:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSeedData = async () => {
        if (!window.confirm('This will import all default services into the database. Continue?')) return;
        setLoading(true);
        try {
            const batchPromises = DEFAULT_SERVICES.map(async (service, index) => {
                let transformedService = {
                    title: service.title,
                    order: index,
                    createdAt: serverTimestamp(),
                    categories: [],
                    items: []
                };

                if (service.categories) {
                    transformedService.categories = service.categories.map(cat => ({
                        name: cat.name,
                        items: cat.items.map(itemStr => ({
                            name: itemStr,
                            description: '',
                            imageUrl: ''
                        }))
                    }));
                } else if (service.items) {
                    // Import as Flat Items for Simple Structure
                    transformedService.items = service.items.map(itemStr => ({
                        name: itemStr,
                        description: '',
                        imageUrl: ''
                    }));
                }

                await addDoc(collection(db, 'services'), transformedService);
            });

            await Promise.all(batchPromises);
            alert('Default services imported successfully!');
            fetchServices();
        } catch (error) {
            console.error('Error seeding data:', error);
            alert('Failed to import data');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (service) => {
        setEditingId(service.id);
        const hasCats = service.categories && service.categories.length > 0;
        // Check if it's the "fake" General category we made previously or a real one?
        // Actually, let's just trust the data structure.
        setIsCategorized(hasCats);

        setFormData({
            title: service.title || '',
            categories: service.categories || [],
            items: service.items || []
        });
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({ title: '', categories: [], items: [] });
    };

    const toggleStructure = () => {
        if (isCategorized) {
            // Converting FROM Categorized TO Simple
            // We need to flatten all items from all categories into one list
            if (window.confirm('Switching to Simple List will merge all items and remove categories. Continue?')) {
                const allItems = [];
                (formData.categories || []).forEach(cat => {
                    if (cat.items) allItems.push(...cat.items);
                });
                setFormData({ ...formData, items: allItems, categories: [] });
                setIsCategorized(false);
            }
        } else {
            // Converting FROM Simple TO Categorized
            // Provide a default "General" category
            setFormData({
                ...formData,
                categories: [{ name: 'General', items: formData.items || [] }],
                items: []
            });
            setIsCategorized(true);
        }
    };

    const handleSave = async () => {
        try {
            // Prepare data based on mode
            const dataToSave = {
                title: formData.title,
                // If categorized, save categories and empty items. If simple, save items and empty categories.
                categories: isCategorized ? formData.categories : [],
                items: isCategorized ? [] : formData.items,
                updatedAt: serverTimestamp()
            };

            if (editingId === 'new') {
                await addDoc(collection(db, 'services'), {
                    ...dataToSave,
                    order: services.length,
                    createdAt: serverTimestamp()
                });
            } else {
                await updateDoc(doc(db, 'services', editingId), dataToSave);
            }
            alert('Service saved successfully!');
            fetchServices();
            handleCancel();
        } catch (error) {
            console.error('Error saving service:', error);
            alert('Failed to save service: ' + error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this service?')) return;
        try {
            await deleteDoc(doc(db, 'services', id));
            fetchServices();
        } catch (error) {
            console.error('Error deleting service:', error);
            alert('Failed to delete service: ' + error.message);
        }
    };

    // --- Category Management ---

    const addCategory = () => {
        setFormData({
            ...formData,
            categories: [...(formData.categories || []), { name: 'New Category', items: [] }]
        });
    };

    const updateCategoryName = (index, name) => {
        const newCats = formData.categories.map((cat, i) =>
            i === index ? { ...cat, name } : cat
        );
        setFormData({ ...formData, categories: newCats });
    };

    const removeCategory = (index) => {
        const newCats = formData.categories.filter((_, i) => i !== index);
        setFormData({ ...formData, categories: newCats });
    };

    // --- Item Management (Nested) ---

    const addItemToCategory = (catIndex) => {
        const newCats = formData.categories.map((cat, i) => {
            if (i === catIndex) {
                return {
                    ...cat,
                    items: [...(cat.items || []), { name: 'New Item', description: '', imageUrl: '' }]
                };
            }
            return cat;
        });
        setFormData({ ...formData, categories: newCats });
    };

    const updateItemInCategory = (catIndex, itemIndex, field, value) => {
        const newCats = formData.categories.map((cat, i) => {
            if (i === catIndex) {
                const newItems = cat.items.map((item, j) =>
                    j === itemIndex ? { ...item, [field]: value } : item
                );
                return { ...cat, items: newItems };
            }
            return cat;
        });
        setFormData({ ...formData, categories: newCats });
    };

    const removeItemFromCategory = (catIndex, itemIndex) => {
        const newCats = formData.categories.map((cat, i) => {
            if (i === catIndex) {
                return {
                    ...cat,
                    items: cat.items.filter((_, j) => j !== itemIndex)
                };
            }
            return cat;
        });
        setFormData({ ...formData, categories: newCats });
    };

    // --- Flat Item Management (Simple List) ---

    const addFlatItem = () => {
        setFormData({
            ...formData,
            items: [...(formData.items || []), { name: 'New Item', description: '', imageUrl: '' }]
        });
    };

    const updateFlatItem = (index, field, value) => {
        const newItems = formData.items.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        setFormData({ ...formData, items: newItems });
    };

    const removeFlatItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };


    // --- Image Compression & Upload ---

    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 800;
                    const MAX_HEIGHT = 800;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Compress to JPEG with 0.7 quality
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    resolve(dataUrl);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleItemImageUpload = async (catIndex, itemIndex, file, isFlat = false) => {
        if (!file) return;

        setImageUploading(true);
        try {
            // Compress Image directly for Firestore
            const compressedBase64 = await compressImage(file);

            // Limit check (Base64 string length ~ size in bytes)
            // 700KB roughly = 700*1024 characters.
            if (compressedBase64.length > 700 * 1024) {
                alert('Even after compression, this image is too complex. Please pick a simpler image.');
                setImageUploading(false);
                return;
            }

            if (isFlat) {
                updateFlatItem(catIndex, 'imageUrl', compressedBase64);
            } else {
                updateItemInCategory(catIndex, itemIndex, 'imageUrl', compressedBase64);
            }
            alert('Image compressed and prepared! Click "Save Service" to finish.');
        } catch (error) {
            console.error('Image processing failed:', error);
            alert('Image processing failed: ' + error.message);
        } finally {
            setImageUploading(false);
        }
    };

    // Component for rendering a single item editor (reusable)
    const ItemEditor = ({ item, index, onUpdate, onRemove, onUpload }) => (
        <div className="item-edit-card" style={{ background: 'white', padding: '20px', marginBottom: '15px', borderRadius: '10px', border: '1px solid #e0e0e0', boxShadow: '0 2px 5px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                <h5 style={{ margin: 0, color: '#888', fontSize: '0.8rem' }}>Item #{index + 1}</h5>
                <button
                    onClick={() => onRemove(index)}
                    style={{ color: '#ff6b6b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                    <FiTrash2 /> Remove
                </button>
            </div>

            <div style={{ display: 'grid', gap: '15px' }}>
                <input
                    type="text"
                    value={item.name}
                    onChange={(e) => onUpdate(index, 'name', e.target.value)}
                    placeholder="Item Name"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <textarea
                    value={item.description}
                    onChange={(e) => onUpdate(index, 'description', e.target.value)}
                    placeholder="Item Description (optional)"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', minHeight: '80px', fontFamily: 'inherit' }}
                />

                <div className="image-control" style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    {item.imageUrl ? (
                        <div style={{ position: 'relative', width: '60px', height: '60px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #ddd' }}>
                            <img src={item.imageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    ) : (
                        <div style={{ width: '60px', height: '60px', borderRadius: '6px', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
                            <FiImage size={24} />
                        </div>
                    )}

                    <div>
                        <label className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '0.9rem', cursor: 'pointer', background: 'white', border: '1px solid #ddd', borderRadius: '6px' }}>
                            <FiImage /> {item.imageUrl ? 'Change Image' : 'Upload Image'}
                            <input
                                type="file"
                                style={{ display: 'none' }}
                                onChange={(e) => onUpload(index, e.target.files[0])}
                                accept="image/*"
                            />
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
            <div className="spinner"></div>
            <p>Loading Services...</p>
        </div>
    );

    const EditForm = () => (
        <div className="edit-form">
            <div className="form-group" style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#444' }}>Service Title</label>
                <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' }}
                    placeholder="Enter service title"
                />
            </div>

            {/* Structure Switcher */}
            <div style={{ marginBottom: '30px', background: '#f8f9fa', padding: '15px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', color: '#555' }}>Service Structure:</span>
                <div style={{ display: 'flex', background: '#e0e0e0', borderRadius: '8px', padding: '4px' }}>
                    <button
                        onClick={isCategorized ? toggleStructure : undefined}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            background: !isCategorized ? 'white' : 'transparent',
                            color: !isCategorized ? 'var(--primary)' : '#666',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: !isCategorized ? 'bold' : 'normal',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: !isCategorized ? '0 2px 5px rgba(0,0,0,0.1)' : 'none'
                        }}
                    >
                        <FiList /> Simple List
                    </button>
                    <button
                        onClick={!isCategorized ? toggleStructure : undefined}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            background: isCategorized ? 'white' : 'transparent',
                            color: isCategorized ? 'var(--primary)' : '#666',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: isCategorized ? 'bold' : 'normal',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: isCategorized ? '0 2px 5px rgba(0,0,0,0.1)' : 'none'
                        }}
                    >
                        <FiGrid /> Categorized
                    </button>
                </div>
            </div>

            {isCategorized ? (
                // Categorized View
                <div className="categories-section">
                    <h4 style={{ marginBottom: '15px', color: '#666', textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.5px' }}>Categories & Items</h4>
                    {(formData.categories || []).map((cat, catIndex) => (
                        <div key={catIndex} className="category-edit-card" style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #eee' }}>
                            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <input
                                        type="text"
                                        value={cat.name}
                                        onChange={(e) => updateCategoryName(catIndex, e.target.value)}
                                        placeholder="Category Name"
                                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontWeight: '600' }}
                                    />
                                </div>
                                <button
                                    onClick={() => removeCategory(catIndex)}
                                    className="btn-icon btn-delete"
                                    title="Delete Category"
                                    style={{ width: '40px', height: '40px', padding: 0 }}
                                >
                                    <FiTrash2 />
                                </button>
                            </div>

                            <div className="items-list" style={{ marginLeft: '10px' }}>
                                {(cat.items || []).map((item, itemIndex) => (
                                    <ItemEditor
                                        key={itemIndex}
                                        item={item}
                                        index={itemIndex}
                                        onUpdate={(idx, field, val) => updateItemInCategory(catIndex, idx, field, val)}
                                        onRemove={(idx) => removeItemFromCategory(catIndex, idx)}
                                        onUpload={(idx, file) => handleItemImageUpload(catIndex, idx, file)}
                                    />
                                ))}
                                <button
                                    onClick={() => addItemToCategory(catIndex)}
                                    style={{ width: '100%', padding: '12px', border: '2px dashed #e0e0e0', background: 'white', borderRadius: '8px', color: 'var(--primary)', fontWeight: '600', cursor: 'pointer', marginTop: '5px' }}
                                >
                                    + Add Item to Category
                                </button>
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={addCategory}
                        className="btn-secondary"
                        style={{ width: '100%', padding: '15px', border: '2px dashed #ccc', background: '#fafafa', borderRadius: '10px', cursor: 'pointer', color: '#666', fontWeight: '600' }}
                    >
                        + Add New Category
                    </button>
                </div>
            ) : (
                // Simple List View
                <div className="simple-list-section">
                    <h4 style={{ marginBottom: '15px', color: '#666', textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.5px' }}>Service Items</h4>

                    {(formData.items || []).map((item, index) => (
                        <ItemEditor
                            key={index}
                            item={item}
                            index={index}
                            onUpdate={(idx, field, val) => updateFlatItem(idx, field, val)}
                            onRemove={(idx) => removeFlatItem(idx)}
                            onUpload={(idx, file) => handleItemImageUpload(idx, null, file, true)}
                        />
                    ))}

                    <button
                        onClick={addFlatItem}
                        style={{ width: '100%', padding: '15px', border: '2px dashed #ccc', background: '#fafafa', borderRadius: '10px', cursor: 'pointer', color: 'var(--primary)', fontWeight: '600', fontSize: '1rem' }}
                    >
                        + Add New Item
                    </button>
                </div>
            )}

            <div className="actions" style={{ marginTop: '30px', display: 'flex', gap: '15px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                <button onClick={handleSave} className="btn-primary" disabled={imageUploading} style={{ padding: '12px 30px' }}>
                    <FiSave /> Save Service
                </button>
                <button onClick={handleCancel} className="btn-secondary" style={{ padding: '12px 30px' }}>
                    <FiX /> Cancel
                </button>
            </div>
        </div>
    );

    return (
        <div className="services-manager">
            <div className="manager-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ fontSize: '1.5rem', color: '#333' }}>Services Management</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {services.length === 0 && (
                        <button className="btn-secondary" onClick={handleSeedData} style={{ marginRight: '10px' }}>
                            Import Default Data
                        </button>
                    )}
                    <button className="btn-primary" onClick={() => handleEdit({ id: 'new', title: 'New Service', categories: [] })}>
                        <FiPlus /> Add New Service
                    </button>
                </div>
            </div>

            <div className="services-list">
                {services.map(service => (
                    <div key={service.id} className="service-admin-card" style={{ background: 'white', border: '1px solid #eee', borderRadius: '16px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        {editingId === service.id ? (
                            <EditForm />
                        ) : (
                            <div className="view-mode" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, var(--primary) 0%, #d4a5a5 100%)',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold'
                                    }}>
                                        <FiActivity size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#333', marginBottom: '5px' }}>{service.title}</h3>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {service.categories && service.categories.length > 0 ? (
                                                <span style={{ fontSize: '0.8rem', color: '#888', background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px' }}>
                                                    {service.categories.length} Categories
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: '0.8rem', color: '#888', background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px' }}>
                                                    {(service.items || []).length} Items (Simple)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="actions" style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        className="btn-icon btn-edit"
                                        onClick={() => handleEdit(service)}
                                        title="Edit Service"
                                    >
                                        <FiEdit2 />
                                    </button>
                                    <button
                                        className="btn-icon btn-delete"
                                        onClick={() => handleDelete(service.id)}
                                        title="Delete Service"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {editingId === 'new' && (
                    <div className="service-admin-card edit-mode" style={{ background: 'white', border: '2px solid var(--primary)', borderRadius: '16px', padding: '25px', marginBottom: '25px', boxShadow: '0 8px 30px rgba(0,0,0,0.1)' }}>
                        <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #eee' }}>
                            <h3 style={{ color: 'var(--primary)', margin: 0 }}>Add New Service</h3>
                        </div>
                        <EditForm />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServicesManager;
